import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Images, Trash2, X, Upload, Heart, MessageCircle, Send } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

export default function AlbumsTab({ userId, isOwn }) {
  const [albums, setAlbums] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/albums/user/${userId}`);
    setAlbums(data);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/albums", { name }); setName(""); setCreating(false); toast.success("Album created"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const removeAlbum = async (id) => {
    try { await api.delete(`/albums/${id}`); toast.success("Album deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="albums-tab">
      {isOwn && (
        <div className="mb-4">
          {creating ? (
            <div className="flex gap-2">
              <Input data-testid="album-name-input" value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Album name (e.g. My Journey)" className="rounded-full" autoFocus />
              <Button data-testid="album-create-confirm" onClick={create} className="bg-copper hover:bg-copper/90 text-white rounded-full">Create</Button>
              <Button variant="ghost" onClick={() => setCreating(false)} className="rounded-full">Cancel</Button>
            </div>
          ) : (
            <Button data-testid="new-album-btn" onClick={() => setCreating(true)} variant="outline" className="rounded-full border-copper/40 text-copper hover:bg-copper/10">
              <Plus className="w-4 h-4 mr-1.5" /> New album
            </Button>
          )}
        </div>
      )}

      {albums.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No albums yet.{isOwn ? " Create one to group your photos and videos." : ""}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {albums.map((a) => (
            <div key={a.album_id} data-testid="album-card" className="group relative bg-card rounded-2xl border border-border overflow-hidden cursor-pointer hover:shadow-lg transition-all"
              onClick={() => setActive(a.album_id)}>
              <div className="aspect-square bg-secondary grid grid-cols-2 grid-rows-2 gap-0.5">
                {a.preview?.length ? a.preview.slice(0, 4).map((u, i) => (
                  <img key={i} src={fileUrl(u)} alt="" className="w-full h-full object-cover" />
                )) : <div className="col-span-2 row-span-2 flex items-center justify-center text-muted-foreground"><Images className="w-8 h-8" /></div>}
              </div>
              <div className="p-3">
                <p className="font-serif font-semibold text-bronze truncate">{a.name}</p>
                <p className="text-xs text-muted-foreground">{a.item_count} item{a.item_count === 1 ? "" : "s"}</p>
              </div>
              {isOwn && (
                <button onClick={(e) => { e.stopPropagation(); removeAlbum(a.album_id); }}
                  className="absolute top-2 right-2 bg-black/40 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {active && <AlbumView albumId={active} isOwn={isOwn} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function AlbumView({ albumId, isOwn, onClose }) {
  const [album, setAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const { data } = await api.get(`/albums/${albumId}`);
    setAlbum(data);
    setLightbox((lb) => (lb ? data.media.find((m) => m.media_id === lb.media_id) || null : null));
  }, [albumId]);
  useEffect(() => { load(); }, [load]);

  const addMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/albums/${albumId}/media`, { url: data.url, media_type: data.media_type });
      }
      toast.success("Added to album"); load();
    } catch (err) { toast.error(apiErr(err)); }
    setUploading(false);
  };

  const removeMedia = async (id) => {
    try { await api.delete(`/media/${id}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="album-view" className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-bronze">{album?.name || "Album"}</DialogTitle></DialogHeader>
        {isOwn && (
          <div>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={addMedia} data-testid="album-media-input" />
            <Button data-testid="album-add-media-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-copper hover:bg-copper/90 text-white rounded-full">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Add photos / videos"}
            </Button>
          </div>
        )}
        {!album?.media?.length ? (
          <p className="text-center text-muted-foreground py-10">This album is empty.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {album.media.map((m) => (
              <div key={m.media_id} className="relative group rounded-xl overflow-hidden bg-secondary cursor-pointer" onClick={() => setLightbox(m)}>
                {m.media_type === "video"
                  ? <video src={fileUrl(m.url)} className="w-full h-32 object-cover" />
                  : <img src={fileUrl(m.url)} alt="" className="w-full h-32 object-cover" />}
                <div className="absolute bottom-1 left-1 flex items-center gap-2 text-white text-xs">
                  {(m.likes?.length > 0) && <span className="flex items-center gap-0.5 bg-black/40 rounded-full px-1.5 py-0.5"><Heart className="w-3 h-3 fill-white" /> {m.likes.length}</span>}
                  {(m.comments?.length > 0) && <span className="flex items-center gap-0.5 bg-black/40 rounded-full px-1.5 py-0.5"><MessageCircle className="w-3 h-3" /> {m.comments.length}</span>}
                </div>
                {isOwn && (
                  <button onClick={(e) => { e.stopPropagation(); removeMedia(m.media_id); }} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {lightbox && <MediaLightbox media={lightbox} onClose={() => setLightbox(null)} onChange={load} />}
      </DialogContent>
    </Dialog>
  );
}

function MediaLightbox({ media, onClose, onChange }) {
  const { user } = useAuth();
  const [m, setM] = useState(media);
  const [comment, setComment] = useState("");
  useEffect(() => { setM(media); }, [media]);
  const liked = user && m.likes?.includes(user.user_id);

  const toggleLike = async () => {
    if (!user) { toast.error("Sign in to react."); return; }
    try {
      const { data } = await api.post(`/media/${m.media_id}/like`);
      setM((p) => ({ ...p, likes: data.likes }));
      onChange && onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const { data } = await api.post(`/media/${m.media_id}/comment`, { text: comment });
      setM((p) => ({ ...p, comments: data.comments })); setComment(""); onChange && onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="media-lightbox" className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="sr-only">Photo</DialogTitle></DialogHeader>
        {m.media_type === "video"
          ? <video src={fileUrl(m.url)} controls className="w-full rounded-xl max-h-[50vh]" />
          : <img src={fileUrl(m.url)} alt="" className="w-full rounded-xl max-h-[50vh] object-contain bg-secondary" />}
        {m.caption && <p className="text-sm text-foreground/80 mt-2">{m.caption}</p>}
        <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border">
          <button data-testid="media-like-btn" onClick={toggleLike} className={`flex items-center gap-1.5 text-sm ${liked ? "text-copper" : "text-muted-foreground hover:text-copper"}`}>
            <Heart className={`w-5 h-5 ${liked ? "fill-copper" : ""}`} /> {m.likes?.length || 0}
          </button>
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><MessageCircle className="w-5 h-5" /> {m.comments?.length || 0}</span>
        </div>
        <div className="space-y-2 mt-2">
          {m.comments?.map((c) => (
            <div key={c.comment_id} className="flex gap-2 text-sm">
              <img src={fileUrl(c.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${c.user_id}`} alt="" className="w-7 h-7 rounded-full bg-secondary shrink-0" />
              <div className="bg-secondary rounded-2xl px-3 py-1.5"><span className="font-semibold mr-2">{c.name}</span>{c.text}</div>
            </div>
          ))}
        </div>
        {user && (
          <div className="flex gap-2 mt-2">
            <Input data-testid="media-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a kind comment…" className="rounded-full" />
            <Button size="icon" data-testid="media-comment-send" onClick={addComment} className="bg-copper hover:bg-copper/90 rounded-full shrink-0"><Send className="w-4 h-4" /></Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
